import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addCity } from '../store/cityListSlice';
import { getWeather } from '../store/weatherSlice';
import { v4 as uuidv4 } from 'uuid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import debounce from 'lodash.debounce';
import { RootState, AppDispatch } from '../store';

const API_KEY = process.env.REACT_APP_OWM_API_KEY; 

// Тип для опцій міст
interface CityOption {
  name: string;
  country: string;
  state?: string;
  lat: number;
  lon: number;
}

const AddCityForm: React.FC = () => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<CityOption[]>([]);
  const [selected, setSelected] = useState<CityOption | null>(null);
  const dispatch: AppDispatch = useDispatch();
  const citysList = useSelector((state:RootState) => state.cityList);
  const filteredOptions = options.filter(
    (option) =>
      !citysList.some(
        (city: { id: string; name: string }) =>
          city.name.toLowerCase() === option.name.toLowerCase()
      )
  );

  const fetchCities = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 2) {
          setOptions([]);
          return;
        }
        const res = await fetch(
          `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(query)}&limit=20&appid=${API_KEY}`
        );
        const data = await res.json();
        setOptions(
          data.map((item: CityOption) => ({
            name: item.name,
            country: item.country,
            state: item.state,
            lat: item.lat,
            lon: item.lon,
          }))
        );
      }, 400),
    []
  );

  const handleInputChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
  ) => {
    setInput(value);
    fetchCities(value);
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    const newCity = { id: uuidv4(), name: selected.name };
    dispatch(addCity(newCity));
    dispatch(getWeather(newCity));
    setInput('');
    setSelected(null);
  };

  return (
    <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', gap: 2, mb: 2, width: '100%' }}>
      <Autocomplete
        freeSolo
        options={filteredOptions}
        getOptionLabel={option =>
          typeof option === 'string'
            ? option
            : `${option.name}${option.state ? ', ' + option.state : ''}, ${option.country}`
        }
        inputValue={input}
        onInputChange={handleInputChange}
        value={selected}
        onChange={(_, value) => setSelected(value as CityOption | null)}
        filterOptions={x => x}
        sx={{ flex: 1 }}
        renderInput={params => (
          <TextField
            {...params}
            label="City"
            required
            sx={{ flex: 1, background: '#BBFBFF', borderRadius: 1 }}
            fullWidth
          />
        )}
      />
      <Button
        type="submit"
        variant="contained"
        sx={{
          background: 'linear-gradient(90deg, #5409DA 0%, #4E71FF 100%)',
          color: '#fff',
          fontWeight: 600,
          px: 4,
          borderRadius: 1,
          boxShadow: '0 2px 8px rgba(78,113,255,0.15)',
          '&:hover': {
            background: 'linear-gradient(90deg, #4E71FF 0%, #5409DA 100%)',
          },
        }}
      >
        Add
      </Button>
    </Box>
  );
};

export default AddCityForm; 